import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getMemberToken } from '@formosa/shared/auth/tokenStorage';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { discoveryFeedApi } from '../../api/discoveryFeedApi';
import { discoveryPreferencesApi } from '../../api/discoveryPreferencesApi';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { Button, EmptyState, ErrorState, LoadingState } from '../../components/ui';
import { normalizeForYouPage, canShowFeedPreferenceActions, FEED_CONTENT_TYPES } from './discoverModel';
import ForYouCard from './ForYouCard';
import SocialDiscoveryCard from './SocialDiscoveryCard';

const FOR_YOU_LIMIT = 20;

/** 載入更多時保留既有項目（含已更新的 engagement），只附加新的 itemKey */
function appendUnique(current, incoming) {
  const seen = new Set(current.map((item) => item.itemKey));
  return [...current, ...incoming.filter((item) => !seen.has(item.itemKey))];
}

function isCanonicalSocial(item) {
  return item.contentType === FEED_CONTENT_TYPES.SOCIAL_POST && Boolean(item.socialPost);
}

export default function ForYouPanel() {
  const { status, isAuthenticated, user } = useAuth();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const viewerMemberId = user?.memberId ?? user?.id;

  function goLogin() {
    navigate('/login', { state: { from: authReturnLocation(location) } });
  }

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');

  // feedSessionId is retained across pages within this panel mount
  const feedSessionIdRef = useRef(null);
  const generation = useRef(0);

  const load = useCallback(async ({ append = false, cursor = null } = {}) => {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const payload = await discoveryFeedApi.getForYou({
        feedSessionId: feedSessionIdRef.current || undefined,
        cursor: cursor || undefined,
        limit: FOR_YOU_LIMIT,
      });
      if (requestGeneration !== generation.current) return;
      if (payload?.feedSessionId && !feedSessionIdRef.current) {
        feedSessionIdRef.current = payload.feedSessionId;
      }
      const page = normalizeForYouPage(payload);
      setItems((current) => (append ? appendUnique(current, page.items) : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.loadFailed'));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setError('');
    feedSessionIdRef.current = null;
    load();
  }, [load]);

  // Telemetry callbacks passed into each ForYouCard.
  // Cards use useContentVisibilityTelemetry internally and call these on flush.
  const handleFlushImpression = useCallback((payload) => {
    discoveryFeedApi.recordImpression(payload).catch(() => {
      // Impression failures are non-fatal — silently swallow
    });
  }, []);

  const handleFlushBehavior = useCallback((payload) => {
    // 使用已驗證的登入狀態，並在送出前重讀 session，避免登出後的卡片 cleanup 送出請求。
    // token 是否過期或遭撤銷仍由後端判定；匿名 impressions 不受此限制。
    if (status !== 'authenticated' || !getMemberToken()?.trim()) return;
    discoveryFeedApi.recordBehavior(payload).catch(() => {
      // Behavior signal failures are non-fatal — silently swallow
    });
  }, [status]);

  async function handleNotInterested(item) {
    if (!isAuthenticated) {
      goLogin();
      return;
    }
    if (!canShowFeedPreferenceActions({ viewerMemberId, creatorMemberId: item.creatorMemberId })) {
      return;
    }
    try {
      await discoveryPreferencesApi.markNotInterested({
        contentType: item.contentType,
        contentId: item.contentId,
      });
      setItems((current) => current.filter((i) => i.itemKey !== item.itemKey));
    } catch (cause) {
      setError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.actionFailed'));
    }
  }

  async function handleMute(item) {
    if (!isAuthenticated) {
      goLogin();
      return;
    }
    if (!item.creatorMemberId) return;
    if (!canShowFeedPreferenceActions({ viewerMemberId, creatorMemberId: item.creatorMemberId })) {
      return;
    }
    try {
      await discoveryPreferencesApi.muteCreator(item.creatorMemberId);
      setItems((current) => current.filter((i) => i.creatorMemberId !== item.creatorMemberId));
    } catch (cause) {
      setError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.actionFailed'));
    }
  }

  async function handleHide(item) {
    if (!isAuthenticated) {
      goLogin();
      return;
    }
    if (!canShowFeedPreferenceActions({ viewerMemberId, creatorMemberId: item.creatorMemberId })) {
      return;
    }
    try {
      await discoveryPreferencesApi.hideContent({
        contentType: item.contentType,
        contentId: item.contentId,
      });
      setItems((current) => current.filter((i) => i.itemKey !== item.itemKey));
    } catch (cause) {
      setError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.actionFailed'));
    }
  }

  // SocialDiscoveryCard 已自行呼叫 engagement／偏好 API；以下只同步本地列表，不得再打 API
  function handleSocialEngagementChange(postId, property, active) {
    const count = property === 'liked' ? 'likeCount' : 'saveCount';
    setItems((current) => current.map((item) => {
      if (!isCanonicalSocial(item) || String(item.socialPost.postId) !== String(postId)) return item;
      const engagement = item.socialPost.engagement;
      return {
        ...item,
        socialPost: {
          ...item.socialPost,
          engagement: {
            ...engagement,
            [property]: active,
            [count]: Math.max(0, engagement[count] + (active ? 1 : -1)),
          },
        },
      };
    }));
  }

  function removeSocialPost(postId) {
    const itemKey = `${FEED_CONTENT_TYPES.SOCIAL_POST}:${postId}`;
    setItems((current) => current.filter((item) => item.itemKey !== itemKey));
  }

  function removeCreator(creatorMemberId) {
    if (!creatorMemberId) return;
    setItems((current) => current.filter((item) => (
      String(item.creatorMemberId) !== String(creatorMemberId)
      && String(item.socialPost?.author?.memberId) !== String(creatorMemberId)
    )));
  }

  if (phase === 'loading' || phase === 'idle') {
    return (
      <section className="explore-state" aria-live="polite" data-testid="for-you-loading">
        <LoadingState label={t('travel.discover.loading')} />
      </section>
    );
  }

  if (phase === 'error' && items.length === 0) {
    return (
      <section className="explore-state explore-state--error" data-testid="for-you-error">
        <ErrorState
          title={t('travel.discover.error.title')}
          description={error}
          retryable
          retryLabel={t('travel.common.retry')}
          onRetry={() => load()}
        />
      </section>
    );
  }

  return (
    <>
      {phase === 'ready' && items.length === 0 ? (
        <EmptyState
          className="explore-state"
          data-testid="for-you-empty"
          title={t('travel.discover.empty.title')}
          description={
            isAuthenticated
              ? t('travel.discover.empty.hint')
              : t('travel.discover.empty.guestHint')
          }
          action={(
            <div className="explore-empty-actions">
              <Link className="explore-login__secondary" to="/explore?tab=journeys">
                {t('travel.discover.tabs.journeys')}
              </Link>
              {!isAuthenticated ? (
                <Button type="button" variant="primary" onClick={goLogin}>
                  {t('travel.discoverGuest.cta')}
                </Button>
              ) : null}
            </div>
          )}
        />
      ) : null}
      {error && items.length > 0 ? (
        <div className="explore-inline-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="explore-cards" data-testid="for-you-feed">
        {items.map((item) => (isCanonicalSocial(item) ? (
          <SocialDiscoveryCard
            key={item.itemKey}
            item={item.socialPost}
            feedSessionId={feedSessionIdRef.current}
            sponsored={item.deliveryType === 'SPONSORED'}
            commentHref="#comments"
            onEngagementChange={handleSocialEngagementChange}
            onNotInterested={removeSocialPost}
            onMute={removeCreator}
            onHide={removeSocialPost}
            onFlushBehavior={handleFlushBehavior}
            onFlushImpression={handleFlushImpression}
          />
        ) : (
          // Journey 推薦，以及缺少 Social 投影時的舊版 Social 卡（不顯示 engagement）
          <ForYouCard
            key={item.itemKey}
            item={item}
            isAuthenticated={isAuthenticated}
            viewerMemberId={viewerMemberId}
            feedSessionId={feedSessionIdRef.current}
            onNotInterested={handleNotInterested}
            onMute={handleMute}
            onHide={handleHide}
            onFlushBehavior={handleFlushBehavior}
            onFlushImpression={handleFlushImpression}
          />
        )))}
      </div>
      {hasMore ? (
        <button
          className="explore-load-more"
          type="button"
          disabled={phase === 'loading-more'}
          onClick={() => load({ append: true, cursor: nextCursor })}
        >
          {phase === 'loading-more' ? (t('travel.common.loading') || 'Loading…') : (t('travel.common.loadMore') || 'Load more')}
        </button>
      ) : null}
      {items.length > 0 && !hasMore && phase === 'ready' ? (
        <p className="explore-end">{t('travel.discover.end') || "You're all caught up."}</p>
      ) : null}
    </>
  );
}
